// apps/console/src/hooks/useLiveChannel.ts
//
// Operator-side React hook for the robot realtime channel. Opens a WebSocket to
// the broker (URL from runtime config), authenticates with the operator JWT,
// subscribes to the requested topics, applies snapshots, dedups the §8
// persist-then-fanout duplicate window, and dispatches live frames into the
// DashboardBridge. Reconnects with capped backoff + jitter.
//
// The JWT comes ONLY from localStorage (auth service getAccessToken). NEVER
// import an @aws-sdk Cognito client here — it SIGKILLs the vitest CI workers.

import { useCallback, useEffect, useRef, useState } from 'react'

import { parseTopic, type Envelope } from '@libs/realtime'

import { useDashboardBridgeOptional } from '../components/organisms/Dashboard/useDashboardBridge'
import { emitRobotAlert, seedRobotAlerts, type RobotAlertItem } from '../services/api/robot-alerts-store'
import { emitRobotMediaStored } from '../services/api/robot-media-events'
import { emitRobotPosition, seedRobotPositions, type RobotPosition } from '../services/api/robot-positions-store'
import { emitRobotTelemetry } from '../services/api/robot-telemetry-store'
import { getAccessToken, refreshToken } from '../services/auth'
import { getLiveChannelUrl } from '../services/auth/config'
import {
  authFrame,
  pongFrame,
  publishFrame,
  reconnectDelayMs,
  SeqHighWater,
  snapshotRows,
  subscribeFrame,
  unsubscribeFrame,
  type LiveSnapshotRow,
} from './live-channel-socket'

export type LiveChannelStatus = 'disconnected' | 'connecting' | 'authenticating' | 'subscribed' | 'reconnecting'

export interface UseLiveChannelOptions {
  topics: string[]
  /** Called for every live (non-snapshot) data frame AFTER seq dedup. */
  onMessage?: (frame: Envelope) => void
  /** Called for every `error` frame the broker sends back (e.g. a rejected command). */
  onError?: (frame: Envelope) => void
  /**
   * Called for every `ack` a ROBOT sent back about a command this connection
   * issued. The broker already correlates it (`handleAck` → `resolveAck`) and
   * forwards it to the issuing connection only; before this the frame arrived
   * and fell through to the `default` branch, so every robot control could only
   * ever say "sent". `frame.reId` is the id `publish` returned.
   */
  onAck?: (frame: Envelope) => void
  /** Defaults to true. When false (or when no broker URL is configured) the hook stays disconnected. */
  enabled?: boolean
}

export interface UseLiveChannelResult {
  status: LiveChannelStatus
  snapshot: LiveSnapshotRow[]
  lastSnapshotAt: number | null
  subscribe: (topic: string) => void
  unsubscribe: (topic: string) => void
  /** Returns the frame id, so a caller can correlate the robot's `ack` to it. */
  publish: (topic: string, payload: unknown) => string
}

/** The robot up-frame types we surface to onMessage (and, for some, the bridge). */
const ROBOT_FRAME_TYPES = ['location', 'telemetry', 'lidar', 'alert'] as const
type RobotFrameType = (typeof ROBOT_FRAME_TYPES)[number]

function isRobotFrameType(type: string): type is RobotFrameType {
  return (ROBOT_FRAME_TYPES as readonly string[]).includes(type)
}

/** Map an `alert`-kind snapshot row / live alert payload to the store item. */
function toAlertItem(robotId: string, p: Record<string, unknown>): RobotAlertItem {
  return {
    alertId: (p.alertId as string | undefined) ?? '',
    robotId,
    type: (p.type as string | undefined) ?? undefined,
    severity: (p.severity as RobotAlertItem['severity']) ?? undefined,
    identity: (p.identity as string | undefined) ?? undefined,
    message: (p.message as string | undefined) ?? undefined,
    name: (p.name as string | undefined) ?? undefined,
    // The broker fans live alert frames VERBATIM, so a LIVE alert carries the
    // robot wire field `snapshot` (the gisify media key); backfill/snapshot rows
    // read from Mongo carry `snapshotRef`. Read `snapshot` first, fall back to
    // `snapshotRef` so both the live path and the backfill path keep working.
    snapshotRef: (p.snapshot as string | undefined) ?? (p.snapshotRef as string | undefined) ?? undefined,
    recordedAt: (p.recordedAt as string | undefined) ?? undefined,
    // presence-dedup: the enriched live frame (alertFrame) and the backfill
    // snapshot row both carry lastSeenAt (ISO) + sightingCount.
    lastSeenAt: (p.lastSeenAt as string | undefined) ?? undefined,
    sightingCount: typeof p.sightingCount === 'number' ? p.sightingCount : undefined,
    // Both the enriched live frame and the snapshot row carry the fix; this mapper was the one
    // adapter dropping it (the 5s poll keeps it by cast). Only the snapshot row carries
    // `sightings` — leaving it undefined on a live frame is what lets the store's overlay keep
    // the occurrence list the poll delivered.
    lng: typeof p.lng === 'number' && Number.isFinite(p.lng) ? p.lng : undefined,
    lat: typeof p.lat === 'number' && Number.isFinite(p.lat) ? p.lat : undefined,
    sightings: Array.isArray(p.sightings) ? p.sightings.filter((s): s is string => typeof s === 'string') : undefined,
    // The snapshot backfill carries it for an alarm that already rang out, so a reloaded console
    // never rings that alarm again.
    missedAt: typeof p.missedAt === 'string' ? p.missedAt : undefined,
    // And for one an operator declined, so a reload never lists it as missed.
    declinedAt: typeof p.declinedAt === 'string' ? p.declinedAt : undefined,
    status: (p.status as string | undefined) ?? 'open',
  }
}

/** Map a `location`-kind snapshot row / live location payload to the positions
 *  store item. Tolerates missing optional fields. */
function toPositionItem(robotId: string, p: Record<string, unknown>): RobotPosition {
  return {
    robotId,
    name: (p.name as string | undefined) ?? undefined,
    lng: Number(p.lng),
    lat: Number(p.lat),
    heading: typeof p.heading === 'number' ? p.heading : undefined,
    battery: typeof p.battery === 'number' ? p.battery : undefined,
    recordedAt: (p.recordedAt as string | undefined) ?? undefined,
    seq: typeof p.seq === 'number' ? p.seq : undefined,
  }
}

export function useLiveChannel(options: UseLiveChannelOptions): UseLiveChannelResult {
  const { topics, onMessage, onError, onAck, enabled = true } = options
  const bridge = useDashboardBridgeOptional()

  const [status, setStatus] = useState<LiveChannelStatus>('disconnected')
  const [snapshot, setSnapshot] = useState<LiveSnapshotRow[]>([])
  const [lastSnapshotAt, setLastSnapshotAt] = useState<number | null>(null)

  // Mutable refs survive reconnects without re-subscribing the effect.
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptRef = useRef(0)
  const closedByUserRef = useRef(false)
  const seqRef = useRef(new SeqHighWater())
  // Set when the broker rejects our JWT as stale/invalid. The next reconnect
  // mints a fresh token FIRST — see scheduleReconnect.
  const needsTokenRefreshRef = useRef(false)

  // Keep the latest topics/onMessage/bridge readable from the socket callbacks
  // without re-opening the socket on every render.
  const topicsRef = useRef(topics)
  topicsRef.current = topics
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const onAckRef = useRef(onAck)
  onAckRef.current = onAck
  const bridgeRef = useRef(bridge)
  bridgeRef.current = bridge

  /**
   * Whether the broker has WELCOMED this socket — not merely whether it is open.
   *
   * A ref, not the `status` state, because `send` must stay identity-stable: it is a dependency of
   * `publish`/`subscribe`, which callers hold across renders, and re-creating it mid-call would
   * re-run their effects.
   */
  const authedRef = useRef(false)

  /**
   * OPEN IS NOT ENOUGH — the broker's first message MUST be `auth`, and nothing else goes out
   * before `welcome`.
   *
   * The broker dispatches frames fire-and-forget and its `handleAuth` awaits a real credential
   * lookup. A second frame arriving during that await used to find no registry entry and be
   * answered `bad_envelope` — FATAL: the broker closed the socket, so a consumer publishing between
   * `onopen` and `welcome` killed the whole channel, alerts and all. The broker now keeps such
   * frames until `welcome` (`@libs/realtime` `PreAuthGate`), but only as defence in depth: it runs a
   * late subscribe/ping, REFUSES every command except an E-stop (`rate_limited` — a late command is
   * unsafe), and fails a socket that sends too much. So this guard stays the contract.
   *
   * That window is small and easy to hit: the SOS call panel opens this socket and publishes
   * `call_prepare` in the same commit, and once microphone permission is already granted the
   * publish wins the race routinely. Keeping non-auth frames off the wire until `welcome` fixes it
   * for every consumer rather than one call site at a time.
   */
  const send = useCallback((frame: Envelope) => {
    const ws = socketRef.current

    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (frame.type !== 'auth' && !authedRef.current) return

    ws.send(JSON.stringify(frame))
  }, [])

  const dispatchRobotFrame = useCallback((frame: Envelope) => {
    const type = frame.type
    if (!isRobotFrameType(type)) return
    const payload = (frame.payload ?? {}) as Record<string, unknown>
    // The broker fans an up-frame VERBATIM — it does NOT stamp robotId onto it —
    // and a robot may omit robotId on its payload too (e.g. telemetry). Fall back
    // to the topic (`robot:{id}:{channel}`), which is always present, so a frame
    // is never dropped for a missing robotId.
    const topicParsed = frame.topic ? parseTopic(frame.topic) : null
    const topicRobotId = topicParsed?.kind === 'robot' ? topicParsed.robotId : undefined
    const robotId = (frame.robotId ?? (payload.robotId as string | undefined) ?? topicRobotId ?? '') as string
    if (!robotId) return

    // §8 dedup: drop frames whose seq is at/below the snapshot high-water.
    if (!seqRef.current.accept(robotId, type, frame.seq)) return

    onMessageRef.current?.(frame)

    // Feed the open-alert store even when no DashboardBridge is mounted (e.g.
    // the AppLayout mount in Task 4.6 has no provider). The store, not the
    // bridge, backs the operator alert inbox.
    if (type === 'alert') {
      emitRobotAlert(toAlertItem(robotId, payload))
    }

    // Feed the fleet-positions store on every live location frame, symmetric to
    // the alert store above — bridgeless too, since the fleet_map subscribes to
    // the store (not the bridge) for marker movement. The §6 seq lives on the
    // frame envelope (not the payload) for live frames, so thread frame.seq in —
    // exactly like the bridge robot:location emit below uses seq: frame.seq.
    if (type === 'location') {
      emitRobotPosition(toPositionItem(robotId, { ...payload, seq: frame.seq }))
    }

    // The broker's "a capture from this helmet was just stored" notice rides the helmet's own lidar
    // channel (`kind: 'media'`). Bridgeless for the same reason as the stores above: it arrives on
    // the AppLayout fleet mount, and the media panel subscribes to the store.
    if (type === 'lidar' && payload.kind === 'media') {
      emitRobotMediaStored({ robotId, kind: String(payload.mediaKind ?? '') })
    }

    const b = bridgeRef.current
    if (!b) return

    if (type === 'location') {
      b.emit('robot:location', {
        robotId,
        lng: Number(payload.lng),
        lat: Number(payload.lat),
        heading: (payload.heading as number | null | undefined) ?? null,
        battery: (payload.battery as number | null | undefined) ?? null,
        ctrlMode: (payload.ctrlMode as number | null | undefined) ?? null,
        name: (payload.name as string | null | undefined) ?? null,
        seq: frame.seq,
      })
    } else if (type === 'telemetry') {
      // The bridge event forwards `metrics`, the shape an AGIBOT robot sends. A helmet sends its
      // reading AS the payload (battery, worn, vitals, gas, signal), so that shape alone drops
      // everything a helmet reports. Keep the bridge event exactly as it was — existing robot
      // consumers depend on it — and additionally park the whole payload in the telemetry store,
      // which is what the fleet-map details panel reads.
      emitRobotTelemetry(robotId, payload)

      b.emit('robot:telemetry', {
        robotId,
        seq: frame.seq,
        metrics: (payload.metrics as Record<string, number | string | boolean | null> | undefined) ?? undefined,
      })
    } else if (type === 'lidar') {
      // lidar is consumed only via onMessage (the radar hook) — no DashboardBridge event — with
      // one exception below, handled above the bridge check because it must work bridgeless.
    } else if (type === 'alert') {
      b.emit('robot:alert', {
        robotId,
        seq: frame.seq,
        alertId: (payload.alertId as string | undefined) ?? undefined,
        type: (payload.type as string | undefined) ?? undefined,
        severity: payload.severity as 'info' | 'warning' | 'critical' | undefined,
        identity: (payload.identity as string | undefined) ?? undefined,
        message: payload.message as string | undefined,
        name: (payload.name as string | undefined) ?? undefined,
        // Live alert frames are fanned VERBATIM → the wire field is `snapshot`;
        // fall back to `snapshotRef` for parity with the store mapping above.
        snapshotRef:
          (payload.snapshot as string | undefined) ?? (payload.snapshotRef as string | undefined) ?? undefined,
        recordedAt: (payload.recordedAt as string | undefined) ?? undefined,
        status: (payload.status as string | undefined) ?? undefined,
        lng: payload.lng as number | undefined,
        lat: payload.lat as number | undefined,
      })
    }
  }, [])

  const handleFrame = useCallback(
    (frame: Envelope) => {
      switch (frame.type) {
        case 'welcome': {
          attemptRef.current = 0
          authedRef.current = true
          setStatus('subscribed')
          for (const topic of topicsRef.current) send(subscribeFrame(topic))
          break
        }

        case 'snapshot': {
          const rows = snapshotRows(frame)
          const kind = (frame.payload as { kind?: string } | undefined)?.kind

          if (kind === 'alert') {
            // Alert snapshot backfills the open-alert store; it does NOT touch
            // the location snapshot state (which seeds the seq high-water).
            const robotIdOf = (r: unknown) => String((r as { robotId?: string }).robotId ?? '')

            // No scope is threaded: the store only ever ADDS from a snapshot, so a `robot:{id}:alert`
            // subscribe (ONE device's alerts) and the broker's capped fleet snapshot are both safe
            // to fold into the fleet-global store — neither can drop what it fails to mention.
            seedRobotAlerts(rows.map(r => toAlertItem(robotIdOf(r), r as unknown as Record<string, unknown>)))
            break
          }

          // Seed ONLY the `location` high-water. A location snapshot's `seq`
          // counts the LOCATION channel; the robot's telemetry / lidar / alert
          // counters are independent and typically far behind it. Seeding all
          // four (as this did) meant a single location snapshot could set a
          // high-water above the live telemetry seq and DROP every telemetry
          // frame until the robot's counter caught up — starving battery,
          // `estopped` and `depth_reflex`, and the whole lidar tunnel (video,
          // depth radar, obstacles, map keyframes) with it.
          seqRef.current.seedFromSnapshot(rows, ['location'])
          setSnapshot(rows)
          setLastSnapshotAt(Date.now())

          // Symmetric to the alert-snapshot seed above: a FLEET-scoped kind:'location'
          // snapshot (the broker fleet snapshot, S2) REPLACES the held fleet positions so the
          // fleet_map fills on load, not only after each robot's next live frame.
          //
          // A ROBOT-scoped snapshot must NOT replace it. Subscribing to any `robot:{id}:*` topic
          // draws a snapshot for that one robot, and the broker returns `[]` for a robot with no
          // `serverId` bound or an empty PostGIS mirror. Seeding that wholesale emptied the store,
          // so every marker vanished from the fleet map the moment an operator selected a robot —
          // and even a NON-empty robot snapshot would have dropped every other robot's marker.
          // Upsert those rows instead: one robot's snapshot can only ever speak for that robot.
          const positionRows = rows.map(r => toPositionItem(String(r.robotId), r as unknown as Record<string, unknown>))
          const scope = frame.topic ? parseTopic(frame.topic) : null

          if (scope?.kind === 'robot') {
            for (const row of positionRows) emitRobotPosition(row)
          } else {
            seedRobotPositions(positionRows)
          }

          break
        }

        case 'location':
        case 'telemetry':
        case 'lidar':
        case 'alert':
          dispatchRobotFrame(frame)
          break
        case 'ack':
          // Acks were dropped on the floor until the SOS voice call needed them. Two things now
          // arrive this way and neither has any other channel: the device's own acknowledgement of
          // a command, and the broker's SYNTHESISED call acks — `prepared`, `joined`, `unanswered`,
          // `ended`, `failed` — which are the only way an operator learns that a call they placed
          // was answered, refused, or quietly timed out. Routed verbatim; correlating by `reId` is
          // the caller's job, because only it knows which command it issued.
          //
          // BOTH handlers fire, and this arm is deliberately the only `case 'ack'` in the switch.
          // Two consumers want the same frame — the SOS call reads it through `onMessage`, the
          // robot command sender through `onAck` to say "took it" / "refused, with the robot's
          // reason". They arrived as separate features and the merge left a SECOND `case 'ack'`
          // further down; JS takes the first arm, so `onAck` was unreachable and the speak panel's
          // confirmation silently never fired. Add a consumer here, never another case.
          onMessageRef.current?.(frame)
          onAckRef.current?.(frame)
          break
        case 'ping':
          // §6 liveness: an operator only consumes (it never publishes after
          // subscribing), so without this pong the broker reaps the connection
          // at 2x heartbeatIntervalMs and we churn a reconnect every ~60s.
          send(pongFrame(frame.id))
          break
        case 'error': {
          // `auth_expired` / `auth_invalid` are FATAL codes: the broker closes
          // right after sending them. Both mean the stored access token is no
          // longer good, so flag a refresh before the next attempt — otherwise
          // we reconnect with the SAME dead token and loop on "reconnecting"
          // forever. (Cognito access tokens live 1 h; the 45-min refresh
          // interval in useAuth doesn't fire while the machine is asleep, so a
          // sleep/wake cycle lands here routinely.)
          //
          // `bad_envelope` joins them, belt-and-braces. It is what the broker replies when the
          // auth frame carries no jwt at all, and it used to fall through to "no client action"
          // and reconnect unchanged forever. `ws.onopen` above now refuses to send an empty
          // token in the first place, so this should be unreachable for that cause — but the
          // broker also returns it for a malformed frame, and a refresh is the right response to
          // both. Reconnecting with the identical credentials never is.
          const code = (frame.payload as { code?: string } | undefined)?.code

          if (code === 'auth_expired' || code === 'auth_invalid' || code === 'bad_envelope') {
            needsTokenRefreshRef.current = true
          }

          onErrorRef.current?.(frame)
          break
        }

        default:
          // subscribed / unsubscribed / pong — no client action here.
          break
      }
    },
    [send, dispatchRobotFrame],
  )

  const connect = useCallback(() => {
    const url = getLiveChannelUrl()

    if (!url) {
      setStatus('disconnected')

      return
    }

    setStatus(attemptRef.current > 0 ? 'reconnecting' : 'connecting')

    let ws: WebSocket

    try {
      ws = new WebSocket(url)
    } catch {
      setStatus('disconnected')

      return
    }

    socketRef.current = ws

    ws.onopen = () => {
      setStatus('authenticating')
      // Cleared BEFORE the auth frame goes out: a reconnected socket is unauthenticated again, and
      // a stale `true` here would let the very first frame after a blip kill the new connection.
      authedRef.current = false
      seqRef.current.reset()
      const jwt = getAccessToken() ?? ''

      // AN EMPTY TOKEN IS A REFRESH, NOT AN ATTEMPT. Sending '' gets `bad_envelope` back — the
      // broker cannot tell us the token is expired when we did not send one — and `bad_envelope`
      // is not one of the codes below that arm a refresh. So the single case that most needs a
      // new token was the one case that never asked for one, and the socket reconnected with the
      // same empty string until somebody gave up and reloaded. Ask first, then connect.
      if (!jwt) {
        needsTokenRefreshRef.current = true
        try {
          ws.close()
        } catch {
          /* onclose still schedules the reconnect, which now refreshes first */
        }

        return
      }

      ws.send(JSON.stringify(authFrame(jwt)))
    }

    ws.onmessage = (ev: MessageEvent) => {
      let frame: Envelope

      try {
        frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as Envelope
      } catch {
        return
      }

      if (frame && typeof frame === 'object') handleFrame(frame)
    }

    const scheduleReconnect = () => {
      socketRef.current = null
      // A closed socket is unauthenticated by definition. Belt-and-braces with the `onopen` clear:
      // whichever runs first, no frame is ever written onto a connection the broker has not
      // welcomed.
      authedRef.current = false
      if (closedByUserRef.current) {
        setStatus('disconnected')

        return
      }

      setStatus('reconnecting')
      const delay = reconnectDelayMs(attemptRef.current)
      attemptRef.current += 1
      reconnectTimerRef.current = setTimeout(() => {
        if (!needsTokenRefreshRef.current) {
          connect()

          return
        }

        // Consume the flag before awaiting so a second fatal close can set it
        // again rather than being swallowed by this in-flight refresh.
        needsTokenRefreshRef.current = false
        void refreshToken()
          .catch(() => null)
          .then(() => {
            // The effect cleanup may have run while the refresh was in flight.
            if (!closedByUserRef.current) connect()
          })
      }, delay)
    }

    ws.onerror = () => {
      // Let onclose drive the reconnect; closing here avoids a double-schedule.
      try {
        ws.close()
      } catch {
        /* ignore */
      }
    }

    ws.onclose = scheduleReconnect
  }, [handleFrame])

  // Open/teardown the socket lifecycle. Re-runs only when enabled flips.
  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected')

      return
    }

    closedByUserRef.current = false
    attemptRef.current = 0
    connect()

    return () => {
      closedByUserRef.current = true
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
      const ws = socketRef.current
      socketRef.current = null
      if (ws) {
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        try {
          ws.close()
        } catch {
          /* ignore */
        }
      }
    }
  }, [enabled, connect])

  const subscribe = useCallback(
    (topic: string) => {
      send(subscribeFrame(topic))
    },
    [send],
  )

  const unsubscribe = useCallback(
    (topic: string) => {
      send(unsubscribeFrame(topic))
    },
    [send],
  )

  const publish = useCallback(
    (topic: string, payload: unknown) => {
      // The id is RETURNED rather than kept here: correlation is the caller's
      // business (it knows which command it sent), and holding a map in the
      // channel would make one shared hook own every surface's pending state.
      const frame = publishFrame(topic, payload)
      send(frame)

      return frame.id
    },
    [send],
  )

  return { status, snapshot, lastSnapshotAt, subscribe, unsubscribe, publish }
}
